import React from 'react';
import { observer } from 'mobx-react';
import { IntlShape } from 'react-intl';
import { CommonModal } from '../../../../global/modal/common/common-modal';
import TroubleshootModalViewModel from './troubleshoot-modal.viewmodel';
import DefaultButton from '../../../../global/default-button/default-button';
import { FormField } from '../../../../global/form/form-field/form-field';
import { CopyButton } from '../../../../global/copy-button/copy-button';
import { ConnectionType } from '../../../../../api/connections.dao';

import './troubleshoot-modal.scss';
import { NOT_AVAILABLE_STRING } from '../../../../../constants/common.constants';
import { DONE_TEXT, LOADING } from '../../../../../constants/common-labels.constants';

interface ITroubleshootModalPresentationProps {
    intl: IntlShape;
    vm: TroubleshootModalViewModel;
}

const TroubleshootModalPresentation = observer(({
    intl,
    vm
}: ITroubleshootModalPresentationProps) => {
    const {
        meetingDetails,
        handleStartMeeting,
    } = vm;

    const isZoomType = vm.connectionType === ConnectionType.Zoom;
    const connectionType = isZoomType ? 'Meeting' : 'Webinar';
    const meetingTopic = vm.connectionType === ConnectionType.Zoom
        ? intl.formatMessage({
            id: 'oe.input.meetingTopic',
            description: 'text',
            defaultMessage: 'Meeting Topic'
        })
        : intl.formatMessage({
            id: 'oe.input.topic',
            description: 'Label text',
            defaultMessage: 'Topic',
        });

    return (
        <CommonModal
            isOpen={vm.isFormShown || vm.isLoading}
            onRequestClose={vm.hideForm}
            className='troubleshoot-meeting-modal'
            title={intl.formatMessage({
                id: `oe.common.troubleshoot${connectionType}`,
                description: 'Button text',
                defaultMessage: 'Troubleshoot {prefix}'
            },
                { prefix: connectionType })}
            footer={(
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultButton
                        onClick={vm.hideForm}
                        text={intl.formatMessage(DONE_TEXT)}
                        modifier={['fat', 'confirm-safe']}
                    />
                </div>
            )}
        >
            <form className='oe-form'>
                {vm.isLoading
                    ? <div className='loading'>
                        {intl.formatMessage(LOADING, {
                            suffix: ' meeting details...'
                        })}
                    </div>
                    : <>
                        <div className='m-bottom-25px flex flex-content-space-between'>
                            <div className='m-bottom-5px width-25'>
                                <span data-test-id='troubleshoot-id-label'>
                                    {intl.formatMessage({
                                        id: `oe.input.${connectionType}Id`,
                                        description: 'text',
                                        defaultMessage: '{prefix} ID'
                                    }, { prefix: connectionType })}
                                </span>
                                <p className='m-top-5px font-700 word-wrap line-height-17px' >{vm.connectionId}</p>
                            </div>
                            <div className='m-bottom-5px width-70'>
                                <span data-test-id='troubleshoot-topic-label'>
                                    {meetingTopic}
                                </span>
                                <p className='m-top-5px font-700 word-wrap line-height-17px' >{vm.meetingTopic}</p>
                            </div>
                        </div>
                        <div className='m-bottom-10px italic'>
                            {intl.formatMessage({
                                defaultMessage: 'If you are having trouble starting the {prefix}, try this refreshed Host Link:',
                                description: 'text',
                                id: `oe.troubleshoot.tryRefresh${connectionType}HostLink`
                            }, { prefix: connectionType })}
                        </div>
                        <FormField
                            className='m-bottom-45px'
                            label={intl.formatMessage({
                                id: 'oe.input.hostLink',
                                defaultMessage: 'Host Link',
                                description: 'Input label'
                            })}
                        >
                            <div className='flex'>
                                <input
                                    value={meetingDetails.startUrl}
                                    readOnly
                                    disabled
                                    type='text'
                                    className='oe-form-field__control'
                                />
                                <DefaultButton
                                    text={intl.formatMessage({
                                        id: `oe.buttons.label.start${connectionType}`,
                                        description: 'text',
                                        defaultMessage: 'Start {prefix}'
                                    },
                                        { prefix: connectionType })}
                                    className='m-left-10px m-right-10px'
                                    onClick={() => handleStartMeeting(meetingDetails.startUrl)}
                                />
                                <CopyButton
                                    copyValue={meetingDetails.startUrl}
                                    text='Copy'
                                    withoutIcon
                                    withCopyLink
                                />
                            </div>
                        </FormField>
                        <div className='m-bottom-10px italic'>
                            {intl.formatMessage({
                                defaultMessage: 'Here is the rest of the key {prefix} information, verified just now.',
                                description: 'text',
                                id: `oe.troubleshoot.restOfTheKey${connectionType}Information`
                            },
                                { prefix: connectionType })}
                        </div>
                        <FormField
                            className='m-bottom-10px'
                            label={intl.formatMessage({
                                id: 'oe.input.connectionLink',
                                defaultMessage: 'Connection Link',
                                description: 'Input label'
                            })}
                        >
                            <div className='flex'>
                                <input
                                    value={meetingDetails.joinUrl}
                                    readOnly
                                    disabled
                                    type='text'
                                    className='oe-form-field__control'
                                />
                                <CopyButton
                                    className='m-left-10px'
                                    text='Copy'
                                    withoutIcon
                                    withCopyLink
                                    copyValue={meetingDetails.joinUrl}
                                    disabled={!meetingDetails.joinUrl}
                                />
                            </div>
                        </FormField>
                        <FormField
                            className='m-bottom-10px'
                            label={intl.formatMessage({
                                id: 'oe.input.connectionPassword',
                                defaultMessage: 'Connection Password',
                                description: 'Input label'
                            })}
                        >
                            <div className='flex'>
                                <input
                                    value={meetingDetails.password}
                                    readOnly
                                    disabled
                                    type='text'
                                    className='oe-form-field__control'
                                />
                                <CopyButton
                                    className='m-left-10px'
                                    text='Copy'
                                    withoutIcon
                                    withCopyLink
                                    copyValue={meetingDetails.password}
                                    disabled={!meetingDetails.password}
                                />
                            </div>
                        </FormField>
                        <FormField
                            className='m-bottom-10px'
                            label={intl.formatMessage({
                                id: 'oe.input.connectionAccount',
                                defaultMessage: 'Connection Account',
                                description: 'Input label'
                            })}
                        >
                            <div className='flex'>
                                <input
                                    value={meetingDetails.zoomUser?.email}
                                    readOnly
                                    disabled
                                    type='text'
                                    className='oe-form-field__control'
                                />
                                <CopyButton
                                    className='m-left-10px'
                                    text='Copy'
                                    withoutIcon
                                    withCopyLink
                                    copyValue={meetingDetails.zoomUser?.email}
                                    disabled={!meetingDetails.zoomUser?.email}
                                />
                            </div>
                        </FormField>
                        <FormField
                            className='m-bottom-10px'
                            label={intl.formatMessage({
                                id: 'oe.input.sip',
                                description: 'Input label',
                                defaultMessage: 'SIP'
                            })}
                        >
                            <div className='flex'>
                                <input
                                    value={meetingDetails.sip}
                                    readOnly
                                    disabled
                                    type='text'
                                    className='oe-form-field__control'
                                />
                                <CopyButton
                                    className='m-left-10px'
                                    text='Copy'
                                    withoutIcon
                                    withCopyLink
                                    copyValue={meetingDetails.sip}
                                    disabled={!meetingDetails.sip}
                                />
                            </div>
                        </FormField>
                        {vm.connectionType === ConnectionType.Webinar &&
                            <FormField
                                className='m-bottom-10px'
                                label={intl.formatMessage({
                                    id: 'oe.input.hostKey',
                                    description: 'Input label',
                                    defaultMessage: 'Host Key'
                                })}
                            >
                                <div className='flex'>
                                    <input
                                        value={meetingDetails.zoomUser?.hostKey ?? NOT_AVAILABLE_STRING}
                                        readOnly
                                        disabled
                                        type='text'
                                        className='oe-form-field__control'
                                    />
                                    <CopyButton
                                        className='m-left-10px'
                                        text='Copy'
                                        withoutIcon
                                        withCopyLink
                                        copyValue={meetingDetails.zoomUser?.hostKey ?? NOT_AVAILABLE_STRING}
                                        disabled={!meetingDetails.zoomUser?.hostKey}
                                    />
                                </div>
                            </FormField>
                        }
                    </>
                }
            </form>
        </CommonModal>
    );
});

export default TroubleshootModalPresentation;
